/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Which browser tab owns the softphone (see TelephonyContext).
 *
 * Twilio rings every registered Device for an identity, so exactly one tab
 * registers. Tabs agree over a BroadcastChannel:
 *  - a new tab asks who leads and claims only if nobody answers;
 *  - two tabs claiming together settle on the lower id, so it cannot flap;
 *  - the leader repeats its claim every few seconds (a heartbeat), and a tab
 *    that hears nothing for a while claims. A leader that crashed, was
 *    discarded or froze without saying goodbye no longer locks every other
 *    tab out of calling;
 *  - takeOver() moves the softphone to this tab on request; every other tab
 *    gives it up whatever its id.
 * Claims carry the leader's page address so a blocked tab can say where the
 * softphone is.
 *
 * Kept free of React so the rules are tested on their own (tabLeader.test.js).
 */

export const HEARTBEAT_MS = 4000;
export const LEADER_TIMEOUT_MS = 13000;
const FIRST_CLAIM_MS = 150;

// Called through wrappers: browsers throw "Illegal invocation" when
// setTimeout runs with an object other than window as `this`.
const defaultTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (id) => clearInterval(id),
};

export function createTabLeader({
  channel,
  myId,
  where = () => "",
  onChange,
  now = () => Date.now(),
  timers = defaultTimers,
}) {
  let claimed = false;
  let claimTimer = null;
  let lastHeard = now();
  let leaderWhere = null;

  const report = () => onChange({ isLeader: claimed, leaderWhere: claimed ? null : leaderWhere });
  const post = (type) => channel.postMessage({ type, id: myId, at: now(), where: where() });
  const cancelClaim = () => {
    if (claimTimer) timers.clearTimeout(claimTimer);
    claimTimer = null;
  };

  const claim = () => {
    cancelClaim();
    claimed = true;
    leaderWhere = null;
    post("leader-claim");
    report();
  };

  const yieldTo = (msg) => {
    cancelClaim();
    claimed = false;
    lastHeard = now();
    leaderWhere = msg.where || null;
    report();
  };

  channel.onmessage = (event) => {
    const msg = event.data || {};
    if (!msg.id || msg.id === myId) return;
    if (msg.type === "leader-claim") {
      if (!claimed || msg.id < myId) yieldTo(msg);
      return;
    }
    if (msg.type === "leader-takeover") {
      yieldTo(msg);
      return;
    }
    if (msg.type === "leader-release" && !claimed) {
      cancelClaim();
      claimTimer = timers.setTimeout(claim, 50 + Math.random() * 100);
      return;
    }
    if (msg.type === "who-is-leader" && claimed) post("leader-claim");
  };

  const tick = timers.setInterval(() => {
    if (claimed) post("leader-claim");
    else if (!claimTimer && now() - lastHeard > LEADER_TIMEOUT_MS) claim();
  }, HEARTBEAT_MS);

  // Ask first; claim only if nobody answers. The timer goes first so an
  // answer, however quickly it arrives, can cancel it.
  claimTimer = timers.setTimeout(claim, FIRST_CLAIM_MS);
  post("who-is-leader");

  return {
    takeOver() {
      channel.postMessage({ type: "leader-takeover", id: myId, at: now(), where: where() });
      claim();
    },
    release() {
      if (claimed) post("leader-release");
      claimed = false;
    },
    stop() {
      cancelClaim();
      timers.clearInterval(tick);
      if (claimed) post("leader-release");
      claimed = false;
    },
  };
}
